import { onboardStaff } from "api/staff";
import { StaffProfile, User } from "api/types";
import classNames from "classnames";
import Checkbox from "components/Checkbox";
import ReusableForm from "components/Form";
import FormFooter from "components/FormFooter";
import Notification, { NotificationProps } from "components/Notification";
import { FieldType, FormData, StepProps } from "components/types";
import { handleResponse } from "components/utility";
import { useState } from "react";
import { UseFormGetValues, UseFormSetValue, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { OccupationOptions } from "routes/dashboard/staff/InviteStaff";
import {
  AUSTRALIA_STATE_OPTIONS,
  countriesOptions,
  getTodayDate,
  languageOptions,
  validateAustralianPhoneNumber,
  validateAustralianTelephoneNumber,
} from "utils/util";

const getFields = (
  getValue: UseFormGetValues<FormData>,
  setValue: UseFormSetValue<FormData>,
  staffProfile: StaffProfile,
  isDisabled: boolean,
  user: User,
  shouldValidate = true,
  isToggled: boolean,
  setResidentialState: (value: string | undefined) => void,
  residentialState: string | undefined,
  watchedFields: FormData
) => [
  {
    name: "title",
    label: "Title *",
    type: FieldType.Select,
    placeholder: "Select",
    requiredCondition: shouldValidate && "This is a required field",
    isHiglighted: isToggled,
    options: [
      { label: "Mr", value: "Mr" },
      {
        label: "Mrs",
        value: "Mrs",
      },
      { label: "Miss", value: "Miss" },
    ],
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    defaultValue: user.title,
    isDisabled: isDisabled,
    hideSearch: true,
  },
  {
    name: "firstName",
    label: "First Name *",
    type: FieldType.Input,
    placeholder: "John",
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    requiredCondition: shouldValidate && "This is a required field",
    isHiglighted: isToggled,
    defaultValue: user.firstName,
    isDisabled: isDisabled,
  },
  {
    name: "lastName",
    label: "Last Name *",
    type: FieldType.Input,
    placeholder: "Smith",
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    requiredCondition: shouldValidate && "This is a required field",
    isHiglighted: isToggled,
    defaultValue: user.lastName,
    isDisabled: isDisabled,
  },
  {
    name: "email",
    label: "Email *",
    type: FieldType.Input,
    placeholder: "johndoe@gmail.com",
    parentFormContainerClassName:
      "md:w-1/4 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    defaultValue: user.email,
    isDisabled: true,
  },
  {
    name: "mobile",
    label: "Mobile *",
    type: FieldType.Input,
    placeholder: "04XXX XXX XXX",
    parentFormContainerClassName:
      "md:w-1/4 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    requiredCondition: shouldValidate && "This is a required field",
    isHiglighted: isToggled,
    defaultValue: user.mobile,
    isDisabled: isDisabled,
    validationFunction: validateAustralianPhoneNumber,
    validationErrorMessage: "Invalid Australian phone number",
  },
  {
    name: "homePhone",
    label: "Home Telephone no.",
    type: FieldType.Input,
    placeholder: "04XXX XXX XXX",
    parentFormContainerClassName:
      "md:w-1/4 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    defaultValue: staffProfile.homePhone,
    isDisabled: Boolean(staffProfile.homePhone) && isDisabled,
    validationFunction: validateAustralianTelephoneNumber,
    validationErrorMessage: "Invalid Australian telephone number",
    requiredCondition:
      Boolean(watchedFields && watchedFields.homePhone) &&
      "Invalid Australian telephone number",
    additionalTriggerRequired: true,
  },
  {
    name: "occupation",
    label: "Occupation *",
    type: FieldType.Select,
    placeholder: "Select",
    options: OccupationOptions,
    parentFormContainerClassName:
      "md:w-1/4 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    defaultValue: staffProfile.occupation,
    isDisabled: true,
  },
  {
    type: FieldType.Paragraph,
    parentFormContainerClassName: "w-full",
    name: "",
    label: "",
    children: (
      <p
        className={classNames(
          "mt-5 text-sm leading-5 font-semibold text-gray-700",
          isDisabled && "!text-gray-400"
        )}
      >
        Residential address *
      </p>
    ),
  },
  {
    name: "residentialAddressBuildingNo",
    label: "Building/House/Unit number",
    type: FieldType.Input,
    placeholder: "2",
    parentFormContainerClassName:
      "md:w-1/5 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    requiredCondition: shouldValidate && "This is a required field",
    isHiglighted: isToggled,
    defaultValue: staffProfile.residentialAddressBuildingNo,
    isDisabled: isDisabled,
  },
  {
    name: "residentialAddressStreetName",
    label: "Street name",
    type: FieldType.Input,
    placeholder: "Rose Street",
    parentFormContainerClassName:
      "md:w-1/5 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    requiredCondition: shouldValidate && "This is a required field",
    isHiglighted: isToggled,
    defaultValue: staffProfile.residentialAddressStreetName,
    isDisabled: isDisabled,
  },
  {
    name: "residentialAddressSuburb",
    label: "Suburb",
    type: FieldType.Input,
    placeholder: "Clayton",
    parentFormContainerClassName:
      "md:w-1/5 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    requiredCondition: shouldValidate && "This is a required field",
    isHiglighted: isToggled,
    defaultValue: staffProfile.residentialAddressSuburb,
    isDisabled: isDisabled,
  },
  {
    name: "residentialAddressState",
    label: "State/Territory",
    type: FieldType.Select,
    placeholder: "Select State/Territory",
    parentFormContainerClassName:
      "md:w-1/5 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    requiredCondition: shouldValidate && "This is a required field",
    isHiglighted: isToggled,
    defaultValue: staffProfile.residentialAddressState,
    isDisabled: isDisabled,
    options: AUSTRALIA_STATE_OPTIONS,
  },
  {
    name: "residentialAddressZip",
    label: "Postcode",
    type: FieldType.Input,
    placeholder: "3001",
    parentFormContainerClassName:
      "md:w-1/5 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    requiredCondition: shouldValidate && "This is a required field",
    isHiglighted: isToggled,
    defaultValue: staffProfile.residentialAddressZip,
    isDisabled: isDisabled,
  },
  {
    type: FieldType.Paragraph,
    parentFormContainerClassName: "w-full",
    name: "",
    label: "",
    children: (
      <div className="flex space-x-4 items-center mt-5 ">
        <p
          className={classNames(
            "text-sm leading-5 font-semibold text-gray-700",
            isDisabled && "!text-gray-400"
          )}
        >
          Mailing address *
        </p>
        <Checkbox
          name="sameAsResidentialAddress"
          label="Same as Residential address"
          className="!mt-0"
          type={FieldType.Checbox}
          isDisabled={isDisabled}
          onChange={(e) => {
            if (e.target.checked) {
              setValue(
                "mailingAddressBuildingNo",
                getValue("residentialAddressBuildingNo")
              );
              setValue(
                "mailingAddressStreetName",
                getValue("residentialAddressStreetName")
              );
              setValue(
                "mailingAddressSuburb",
                getValue("residentialAddressSuburb")
              );
              setValue(
                "mailingAddressState",
                getValue("residentialAddressState")
              );
              setResidentialState(getValue("residentialAddressState"));
              setValue("mailingAddressZip", getValue("residentialAddressZip"));
            } else {
              setValue("mailingAddressBuildingNo", "");
              setValue("mailingAddressStreetName", "");
              setValue("mailingAddressSuburb", "");
              setValue("mailingAddressState", "");
              setValue("mailingAddressZip", "");
              setResidentialState(undefined);
            }
          }}
        />
      </div>
    ),
  },
  {
    name: "mailingAddressBuildingNo",
    label: "Building/House/Unit number",
    type: FieldType.Input,
    placeholder: "2",
    parentFormContainerClassName:
      "md:w-1/5 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    requiredCondition: shouldValidate && "This is a required field",
    isHiglighted: isToggled,
    defaultValue: staffProfile.mailingAddressBuildingNo,
    isDisabled: isDisabled,
  },
  {
    name: "mailingAddressStreetName",
    label: "Street name",
    type: FieldType.Input,
    placeholder: "Rose Street",
    parentFormContainerClassName:
      "md:w-1/5 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    requiredCondition: shouldValidate && "This is a required field",
    isHiglighted: isToggled,
    defaultValue: staffProfile.mailingAddressStreetName,
    isDisabled: isDisabled,
  },
  {
    name: "mailingAddressSuburb",
    label: "Suburb",
    type: FieldType.Input,
    placeholder: "Clayton",
    parentFormContainerClassName:
      "md:w-1/5 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    requiredCondition: shouldValidate && "This is a required field",
    isHiglighted: isToggled,
    defaultValue: staffProfile.mailingAddressSuburb,
    isDisabled: isDisabled,
  },
  {
    name: "mailingAddressState",
    label: "State/Territory",
    type: FieldType.Select,
    placeholder: "Select State/Territory",
    parentFormContainerClassName:
      "md:w-1/5 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    requiredCondition: shouldValidate && "This is a required field",
    isHiglighted: isToggled,
    defaultValue: residentialState || staffProfile.mailingAddressState,
    isDisabled: isDisabled,
    options: AUSTRALIA_STATE_OPTIONS,
  },
  {
    name: "mailingAddressZip",
    label: "Postcode",
    type: FieldType.Input,
    placeholder: "3001",
    parentFormContainerClassName:
      "md:w-1/5 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    requiredCondition: shouldValidate && "This is a required field",
    isHiglighted: isToggled,
    defaultValue: staffProfile.mailingAddressZip,
    isDisabled: isDisabled,
  },
  {
    name: "dob",
    label: "Date of Birth *",
    type: FieldType.Calendar,
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    wrapperClassName: "w-full",
    className: "w-full",
    requiredCondition: shouldValidate && "This is a required field",
    isHiglighted: isToggled,
    defaultValue: staffProfile.dob,
    isDisabled: isDisabled,
    maxDate: getTodayDate(),
  },
  {
    name: "birthCountry",
    label: "Country of Birth *",
    type: FieldType.Select,
    options: countriesOptions,
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    requiredCondition: shouldValidate && "This is a required field",
    isHiglighted: isToggled,
    defaultValue: staffProfile.birthCountry,
    isDisabled: isDisabled,
  },
  {
    name: "gender",
    label: "Gender *",
    type: FieldType.Radio,
    options: [
      { label: "Male", value: "male" },
      {
        label: "Female",
        value: "female",
      },
    ],
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    requiredCondition: shouldValidate && "This is a required field",
    isHiglighted: isToggled,
    defaultValue: staffProfile.gender,
    isDisabled: Boolean(staffProfile.gender) && isDisabled,
  },
  {
    name: "homeRegion",
    label: "Home Region *",
    type: FieldType.Input,
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    requiredCondition: shouldValidate && "This is a required field",
    isHiglighted: isToggled,
    defaultValue: staffProfile.homeRegion,
    isDisabled: Boolean(staffProfile.homeRegion) && isDisabled,
  },
  {
    name: "spokenLanguages",
    label: "Spoken language(s)*",
    type: FieldType.MultiSelect,
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    requiredCondition: shouldValidate && "This is a required field",
    isHiglighted: isToggled,
    options: languageOptions,
    defaultValue: staffProfile.spokenLanguages,
    isDisabled: isDisabled,
  },
  {
    name: "aboriginalTSIStatus",
    label: "I identify as Aboriginal and or TSI *",
    type: FieldType.Radio,
    options: [
      { label: "Aboriginal", value: "Aboriginal" },
      {
        label: "Torres Strait Islander (TSI)",
        value: "Torres Strait Islander (TSI)",
      },
      { label: "Both", value: "Both" },
      { label: "None of the above", value: "None of the above" },
    ],
    isCol: true,
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    requiredCondition: shouldValidate && "This is a required field",
    isHiglighted: isToggled,
    defaultValue: staffProfile.aboriginalTSIStatus,
    isDisabled: Boolean(staffProfile.aboriginalTSIStatus) && isDisabled,
  },
];

const FIELDS_TO_REMOVE = [
  "calendarMonth",
  "calendarYear",
  "sameAsResidentialAddress",
];

export default function Step2({ isCompleted, user, isToggled }: StepProps) {
  const {
    setError,
    watch,
    reset,
    handleSubmit,
    formState,
    getValues,
    setValue,
    ...formMethods
  } = useForm<FormData>();
  const { staffProfile } = user;
  const navigate = useNavigate();
  const [response, setResponse] = useState<NotificationProps | null>(null);
  const [shouldValidate, setShouldValidate] = useState<boolean>();
  const [residentialState, setResidentialState] = useState<string | undefined>(
    undefined
  );
  const watchedFields = watch();

  const onSubmit = async (data: FormData) => {
    setResponse(null);
    const response = await onboardStaff(2, data, "submit", FIELDS_TO_REMOVE);
    const isSuccess = await handleResponse(response, setError, setResponse);
    isSuccess && navigate("/onboarding/staff/step/3");
  };

  const handleButtonClick = async () => {
    setShouldValidate(false);
    setTimeout(() => handleSubmit(saveForLater)(), 100);
  };

  const saveForLater = async (data: FormData) => {
    setResponse(null);
    const response = await onboardStaff(2, data, "save", FIELDS_TO_REMOVE);
    setShouldValidate(true);
    handleResponse(response, setError, setResponse);
  };

  return (
    <div className="bg-white shadow rounded-md">
      <div className="p-6 pr-0 pb-0">
        <div className="flex flex-col ">
          <Notification
            type="warningExclamation"
            message="All mandatory fields must be completed in order to continue to the next step."
            className="!inline-flex !m-0 w-max"
            iconClassName="text-yellow-500"
          />
          {response ? (
            <Notification
              type={response.type}
              message={response.message}
              className="!inline-flex !m-0 !mt-2 w-max"
            />
          ) : (
            <></>
          )}
        </div>
        <ReusableForm
          fields={getFields(
            getValues,
            setValue,
            staffProfile,
            isCompleted,
            user,
            shouldValidate,
            isToggled,
            setResidentialState,
            residentialState,
            watchedFields
          )}
          onSubmit={onSubmit}
          id="save-step2"
          formClassName="pt-1 pb-6"
          formMethods={{
            ...formMethods,
            setError,
            watch,
            reset,
            handleSubmit,
            formState,
            getValues,
            setValue,
          }}
        />
      </div>
      <FormFooter
        saveforLater={handleButtonClick}
        isDisabled={isCompleted}
        formId="save-step2"
        step={2}
      />
    </div>
  );
}
